function closest(target, selector) {
  return target?.closest?.(selector) || null;
}

/**
 * Owns message-level UI actions without taking ownership of session, streaming,
 * attachment, or message persistence lifecycles. Keeping this boundary narrow
 * lets the AI Chat bootstrap remain an assembler of the established facades.
 */
export function createMessageActionController({
  stream,
  documentRef = document,
  locationRef = globalThis.location,
  messageStore,
  sessionStore,
  messageFacade,
  memoryFacade,
  artifactFacade,
  submitFeedback,
  copyToClipboard,
  copyCodeBlock,
  openLightbox,
  renderMessageContent,
  renderTemporaryCopyAction,
  renderFeedbackAction,
  showToast,
  friendlyAgentError,
  retryFailedMessage = null,
  promptForCorrection = null,
  setTimeoutFn = setTimeout,
}) {
  const feedbackMap = new Map();
  const expandedAgentActivities = new Set();
  let bound = false;

  function closeAllCopyMenus() {
    documentRef.querySelectorAll?.('.aichat-copy-menu').forEach((menu) => {
      menu.hidden = true;
    });
  }

  function isAgentActivityExpanded(messageId) {
    return expandedAgentActivities.has(String(messageId));
  }

  function renderFeedbackGroup(group, activeAction = null) {
    group?.querySelectorAll?.('[data-action="up"],[data-action="down"]').forEach((button) => {
      const isActive = button.dataset.action === activeAction;
      button.classList?.toggle?.('aichat-msg-action-btn--active', isActive);
      renderFeedbackAction(button, button.dataset.action, isActive);
    });
  }

  function toggleAgentActivity(button) {
    const messageId = String(button.dataset.msgId || '');
    if (!messageId) return true;
    if (expandedAgentActivities.has(messageId)) expandedAgentActivities.delete(messageId);
    else expandedAgentActivities.add(messageId);
    const message = messageStore.getMessageState().messages.find((item) => String(item.id) === messageId);
    const group = closest(button, '.aichat-msg-group');
    if (message && group) renderMessageContent(group, message);
    return true;
  }

  function toggleCopyMenu(button) {
    const menu = button.nextElementSibling;
    if (!menu?.classList?.contains('aichat-copy-menu')) return true;
    const wasHidden = menu.hidden;
    closeAllCopyMenus();
    menu.hidden = !wasHidden;
    return true;
  }

  function copyPlainText(button) {
    const group = closest(button, '.aichat-msg-group');
    const bubble = group?.querySelector?.('.aichat-msg-content');
    closeAllCopyMenus();
    if (!bubble) return true;
    copyToClipboard(bubble.innerText.trim(), '复制纯文本失败').then((ok) => {
      if (ok) showToast('已复制纯文本');
    });
    return true;
  }

  function copyMarkdown(button) {
    const messageId = button.dataset.msgId;
    closeAllCopyMenus();
    if (!messageId) return true;
    const { messages } = messageStore.getMessageState();
    const message = messages.find((item) => String(item.id) === String(messageId));
    if (!message) return true;
    copyToClipboard(message.content || '', '复制 Markdown 失败').then((ok) => {
      if (ok) showToast('已复制 Markdown 源码');
    });
    return true;
  }

  function copyBubble(button) {
    const group = closest(button, '.aichat-msg-group') || closest(button, '.aichat-msg');
    const bubble = group?.querySelector?.('.aichat-msg-content');
    if (!bubble) return true;
    copyToClipboard(bubble.innerText.trim()).then((ok) => {
      if (!ok) return;
      const restore = renderTemporaryCopyAction(button);
      button.classList?.add?.('aichat-msg-action-btn--active');
      setTimeoutFn(() => {
        restore();
        button.classList?.remove?.('aichat-msg-action-btn--active');
      }, 1500);
    });
    return true;
  }

  function submitPersistentFeedback({ messageId, action, previous, group }) {
    let correctionType = null;
    let note = null;
    if (action === 'down') {
      const ask = promptForCorrection || globalThis.window?.prompt;
      const correction = ask?.('请简要说明哪里有误（可选）。这会关联本次技能版本，便于纠错归因；你也可以随后点“重新生成”。', '');
      if (correction === null) {
        feedbackMap.delete(messageId);
        renderFeedbackGroup(group);
        return;
      }
      note = correction?.trim() || null;
      correctionType = note ? 'other' : 'incomplete';
    }
    submitFeedback({ messageId, feedbackType: action, note, correctionType }).then((result) => {
      if (action === 'down' && result?.can_regenerate) {
        showToast('纠错已关联本次技能版本，可点击“重新生成”复跑', 'success');
      }
    }).catch((error) => {
      console.warn('[aichat] feedback submit failed:', error);
      if (previous) {
        feedbackMap.set(messageId, previous);
        renderFeedbackGroup(group, previous);
      } else {
        feedbackMap.delete(messageId);
        renderFeedbackGroup(group);
      }
      showToast('反馈提交失败，请稍后重试', 'error');
    });
  }

  function toggleFeedback(button, action) {
    const messageId = button.dataset.msgId;
    if (!messageId) return true;
    const group = closest(button, '.aichat-msg-group');
    const previous = feedbackMap.get(messageId);
    if (previous === action) {
      feedbackMap.delete(messageId);
      renderFeedbackGroup(group);
      return true;
    }
    feedbackMap.set(messageId, action);
    renderFeedbackGroup(group, action);
    if (/^\d+$/.test(String(messageId))) {
      submitPersistentFeedback({ messageId, action, previous, group });
    }
    return true;
  }

  function regenerate(button) {
    const messageId = button.dataset.msgId;
    const currentSession = sessionStore.getSessionState().currentSession;
    if (!currentSession || !messageId) return true;
    messageFacade.regenerateAgentMessage({
      sessionId: currentSession.id,
      messageId: Number(messageId),
      onError: (error) => {
        if (error?.name === 'AbortError') return;
        showToast(friendlyAgentError(error), 'error');
      },
      onStreamEvent: (event) => {
        if (event.event === 'tool_result' && event.name === 'update_working_memory' && !event.error_message) {
          memoryFacade.loadMemoryItems();
          artifactFacade.loadArtifacts(currentSession.id);
        }
      },
    }).catch(() => {});
    return true;
  }

  function retry(button, mode) {
    retryFailedMessage?.({ messageId: String(button.dataset.msgId || ''), mode });
    return true;
  }

  function handleAction(button) {
    switch (button.dataset.action) {
      case 'toggle-agent-activity': return toggleAgentActivity(button);
      case 'copy-menu-toggle': return toggleCopyMenu(button);
      case 'copy-text': return copyPlainText(button);
      case 'copy-markdown': return copyMarkdown(button);
      case 'copy':
      case 'copy-bubble': return copyBubble(button);
      case 'up':
      case 'down': return toggleFeedback(button, button.dataset.action);
      case 'regenerate': return regenerate(button);
      case 'edit-retry': return retry(button, 'edit');
      case 'delete-retry': return retry(button, 'delete');
      case 'copy-code-block':
        copyCodeBlock(button);
        return true;
      case 'reload-page':
        locationRef?.reload?.();
        return true;
      default: return false;
    }
  }

  function handleStreamClick(event) {
    const button = closest(event.target, '[data-action]');
    if (button && handleAction(button)) return;
    const image = closest(event.target, '.aichat-msg-img');
    if (image) openLightbox(image.src);
  }

  function handleDocumentClick(event) {
    if (closest(event.target, '.aichat-copy-wrapper')) return;
    if (closest(event.target, '.aichat-copy-menu')) return;
    closeAllCopyMenus();
  }

  function bind() {
    if (bound) return;
    stream?.addEventListener?.('click', handleStreamClick);
    documentRef.addEventListener?.('click', handleDocumentClick);
    bound = true;
  }

  function destroy() {
    if (!bound) return;
    stream?.removeEventListener?.('click', handleStreamClick);
    documentRef.removeEventListener?.('click', handleDocumentClick);
    bound = false;
  }

  return {
    bind,
    destroy,
    closeAllCopyMenus,
    isAgentActivityExpanded,
  };
}
